import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Box,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/system';


export interface SimpleTableColumn {
  id: string;
  label: string;
  align?: 'left' | 'center' | 'right';
  width?: number | string;
  minWidth?: number;
  render?: (value: any, row: any) => React.ReactNode;
}

interface SimpleTableProps<T = any> {
  columns: SimpleTableColumn[];
  rows: T[];
  loading?: boolean;
  emptyMessage?: string;
  size?: 'small' | 'medium';
  getRowId?: (row: T, index: number) => string | number;
  onRowClick?: (row: T) => void;
  sx?: SxProps<Theme>;
  hover?: boolean;
}

const SimpleTable = <T extends Record<string, any>>({
  columns,
  rows,
  loading = false,
  emptyMessage = 'No hay datos disponibles',
  size = 'small',
  getRowId = (row: T, index: number) => row.id || index,
  onRowClick,
  sx = {},
  hover = true,
}: SimpleTableProps<T>) => {
  const handleRowClick = (row: T) => {
    if (onRowClick) {
      onRowClick(row);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <TableContainer sx={{
       "& .MuiTableCell-head": {
      color: "#374151",           // text-gray-700
      fontSize: "0.75rem",        // text-xs
      textTransform: "uppercase", // uppercase
      fontWeight: 600,            // font-semibold
      borderLeft: "none",
      borderRight: "none",
      borderTop: "none",
      borderBottom: "1px solid #e5e7eb", // border-gray-200
      padding: "6px 16px",
    },
    "& .MuiTableCell-root": {
      borderLeft: "none",
      borderRight: "none",
      borderTop: "none",
      borderBottom: "1px solid #e5e7eb", // border-gray-200
      padding: "5px 16px",
      fontSize: "0.875rem", // text-sm
      lineHeight: 1.5,
      fontWeight: 400,
    },
     "& .MuiTableCell-root:not(:last-child)": {
      borderRight: "none",
    },
    // Icon buttons (e.g. in action column or toolbar)
    "& .MuiIconButton-root": {
      padding: "4px",
      "& .MuiSvgIcon-root": {
        fontSize: "1rem",
      },
    },
    ...sx
  }}>
      <Table size={size}>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.id}
                align={column.align || 'left'}
                sx={{
                  fontWeight: 600,
                  width: column.width,
                  minWidth: column.minWidth,
                }}
              >
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {emptyMessage}
                </Typography>
              </TableCell>
            </TableRow>            
          ) : (
            rows.map((row, index) => (
              <TableRow
                key={getRowId(row, index)}
                hover={hover}
                onClick={() => handleRowClick(row)}
                sx={{
                  cursor: onRowClick ? 'pointer' : 'default',
                }}
              >
                {columns.map((column) => (
                  <TableCell key={column.id} align={column.align || 'left'}>
                    {column.render
                      ? column.render(row[column.id], row)
                      : row[column.id]
                    }
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default SimpleTable;